/**
 * Documents attached to an event — the slide deck, the handout, the worksheet.
 *
 * Shared between the browser and the server so the address the page links to
 * and the route that serves it cannot drift apart, and so the size the picker
 * refuses is the size the server refuses. This file carries only what a browser
 * bundle may see: no authorization, no storage keys.
 *
 * Only PDF, for now. It is the one format every slide and document tool exports
 * and every device opens, and one format keeps the safety check to one shape.
 */
export const EVENT_DOCUMENT_CONTENT_TYPE = "application/pdf";

export const MAX_EVENT_DOCUMENT_BYTES = 15 * 1024 * 1024;

/** For copy: "15 MB". Derived so the number cannot drift from the limit. */
export const MAX_EVENT_DOCUMENT_LABEL = `${MAX_EVENT_DOCUMENT_BYTES / (1024 * 1024)} MB`;

/**
 * A base64 payload is about 4/3 the size of the bytes it encodes, plus the data
 * URL prefix. Same arithmetic as image uploads, for the same reason: a file at
 * exactly the limit must not be rejected for being too long as a string.
 */
export const MAX_EVENT_DOCUMENT_BASE64_LENGTH = Math.ceil(MAX_EVENT_DOCUMENT_BYTES * 4 / 3) + 1024;

/**
 * Where a member downloads an event document.
 *
 * Deliberately not under the public `/app-storage/` route: that route serves any
 * key that is not on the private list, to anyone. Documents are for paid members,
 * so they are served by a route of their own that checks who is asking.
 */
export const EVENT_DOCUMENT_DOWNLOAD_PATH = "/member-downloads/event-documents";

export function eventDocumentDownloadHref(documentId: number) {
  return `${EVENT_DOCUMENT_DOWNLOAD_PATH}/${documentId}`;
}

/**
 * The same document served for viewing in place rather than as a download. Same
 * route, same authorization; only the disposition differs, so the viewer on the
 * event page can show the file without the member leaving the page.
 */
export function eventDocumentViewHref(documentId: number) {
  return `${EVENT_DOCUMENT_DOWNLOAD_PATH}/${documentId}/view`;
}

/** "2.4 MB", "640 KB". For the list a member sees before deciding to download. */
export function formatEventDocumentSize(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/**
 * The title a document gets when the administrator does not type one: the file
 * name without its extension, with the separators people put in file names read
 * as spaces. "Q3-hiring_playbook.pdf" becomes "Q3 hiring playbook".
 */
export function defaultEventDocumentTitle(fileName: string) {
  const stem = fileName.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return stem || "Event document";
}
